import {
  RiCircleLine,
  RiCheckboxCircleFill,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiDonutChartLine,
  RiForbid2Line,
  RiIndeterminateCircleLine,
} from '@remixicon/react';
import {
  type CheckDefinition,
  type CheckResult,
  type TrackDefinition,
  type TrackLevelImportance,
  type TrackLevelResult,
  type TrackResult,
  computeTrackResult,
} from '@internal/backstage-plugin-pulse-common';
import type { Entity } from '@backstage/catalog-model';
import {
  getCheckApplicability,
  type CheckApplicability,
} from '../entity-card-checks/check-applicability';

export const computeEntityTrackResult = (
  track: TrackDefinition,
  entity: Entity,
  entityRef: string,
  checkResults: CheckResult[],
  checkDefs: CheckDefinition[],
  additionalExcludedCheckIds: ReadonlySet<string> = new Set(),
): TrackResult => {
  const checkDefById = new Map(
    checkDefs.map(checkDef => [checkDef.id, checkDef]),
  );
  const excludedCheckIds = new Set<string>(
    track.levels
      .flatMap(level => level.checkIds)
      .filter(checkId => {
        const applicability = getCheckApplicability(
          checkDefById.get(checkId),
          entity,
        );
        return applicability !== 'applicable';
      }),
  );

  for (const checkId of additionalExcludedCheckIds) {
    excludedCheckIds.add(checkId);
  }

  return computeTrackResult(track, entityRef, checkResults, excludedCheckIds);
};

/** Filters out tracks marked inactive, shared across all entity-facing Pulse views. */
export const selectActiveTracks = (
  tracks: TrackDefinition[],
): TrackDefinition[] => tracks.filter(track => !track.inactive);

const LEVEL_IMPORTANCE_LABELS: Record<TrackLevelImportance, string> = {
  mandatory: 'Mandatory',
  recommended: 'Recommended',
};

/** Human-readable label for a level's importance tag. */
export const getLevelImportanceLabel = (
  importance: TrackLevelImportance,
): string => LEVEL_IMPORTANCE_LABELS[importance];

/**
 * Resolves the human-readable name for a cumulative achieved-level ordinal.
 * `0` renders as "No Level"; unknown ordinals fall back to `Level N`.
 */
export const getAchievedLevelName = (
  track: TrackDefinition,
  achievedLevel: number,
): string => {
  if (achievedLevel === 0) {
    return 'No Level';
  }
  const level = track.levels.find(item => item.ordinal === achievedLevel);
  return level
    ? getLevelImportanceLabel(level.importance)
    : `Level ${achievedLevel}`;
};

/**
 * Returns the appropriate icon for a check list row based on its result:
 * unknown (not yet evaluated), passed, or failed.
 */
export const getCheckIcon = ({
  checkResult,
  size,
  applicability,
  isManual,
}: {
  checkResult: CheckResult | undefined;
  size?: number;
  applicability?: CheckApplicability;
  isManual?: boolean;
}) => {
  if (applicability === 'excluded') {
    return <RiForbid2Line color="var(--bui-fg-secondary)" size={size} />;
  }
  if (!checkResult) {
    if (isManual) {
      return <RiCircleLine color="var(--bui-fg-secondary)" size={size} />;
    }
    return (
      <RiIndeterminateCircleLine color="var(--bui-fg-secondary)" size={size} />
    );
  }
  if (checkResult.passed) {
    return <RiCheckboxCircleLine color="var(--bui-fg-positive)" size={size} />;
  }
  return <RiCloseCircleLine color="var(--bui-fg-negative)" size={size} />;
};

/** Certification states an entity can be in for a track, ordered from least
 * to most complete. */
export type TrackRecognitionState =
  | 'not-passing'
  | 'in-progress'
  | 'compliant'
  | 'fully-compliant';

export type TrackRecognition = {
  state: TrackRecognitionState;
  label: string;
  icon: JSX.Element;
  badgeStyle: {
    background: string;
    color: string;
    fontWeight: string;
    whiteSpace: string;
  };
};

const getLevelsByImportance = (
  track: TrackDefinition,
  importance: TrackLevelImportance,
) => track.levels.filter(level => level.importance === importance);

const levelResultsByOrdinal = (trackResult: TrackResult) =>
  new Map(trackResult.levels.map(level => [level.ordinal, level]));

const levelsAllPass = (
  levels: TrackDefinition['levels'],
  resultByOrdinal: Map<number, TrackLevelResult>,
): boolean =>
  levels.every(level => resultByOrdinal.get(level.ordinal)?.passed === true);

export const LEVEL_IMPORTANCE_BADGE_STYLE = {
  background: 'var(--bui-bg-neutral-2)',
  color: 'var(--bui-fg-secondary)',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};

/**
 * Derives the certification state for a track from level importance and
 * pass results. Passing every mandatory check is compliant; passing every
 * mandatory and recommended check is fully compliant.
 */
export const getTrackRecognitionState = (
  track: TrackDefinition,
  trackResult: TrackResult,
): TrackRecognitionState => {
  const resultByOrdinal = levelResultsByOrdinal(trackResult);
  const mandatoryPassed =
    trackResult.trackPassed ??
    levelsAllPass(getLevelsByImportance(track, 'mandatory'), resultByOrdinal);

  if (trackResult.overallPassedChecks === 0) {
    return 'not-passing';
  }
  if (!mandatoryPassed) {
    return 'in-progress';
  }
  if (trackResult.overallPassedChecks < trackResult.overallTotalChecks) {
    return 'compliant';
  }
  return 'fully-compliant';
};

const SUCCESS_BADGE_STYLE = {
  background: 'var(--bui-positive-bg-subdued)',
  color: 'var(--bui-positive-fg-subdued)',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};
const WARNING_BADGE_STYLE = {
  background: 'var(--bui-warning-bg-subdued)',
  color: 'var(--bui-warning-fg-subdued)',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};
const DANGER_BADGE_STYLE = {
  background: 'var(--bui-negative-bg-subdued)',
  color: 'var(--bui-negative-fg-subdued)',
  fontWeight: 'bold',
  whiteSpace: 'nowrap',
};

const TRACK_RECOGNITION_LABELS: Record<TrackRecognitionState, string> = {
  'not-passing': 'Not compliant',
  'in-progress': 'In progress',
  compliant: 'Compliant',
  'fully-compliant': 'Fully compliant',
};

/**
 * Resolves the icon, label, and badge colors representing an entity's
 * certification state for a track (see {@link getTrackRecognitionState}).
 */
export const getTrackRecognition = (
  track: TrackDefinition,
  trackResult: TrackResult,
): TrackRecognition => {
  const state = getTrackRecognitionState(track, trackResult);
  const label = TRACK_RECOGNITION_LABELS[state];

  switch (state) {
    case 'fully-compliant':
      return {
        state,
        label,
        icon: <RiCheckboxCircleFill color="var(--bui-positive-fg-subdued)" />,
        badgeStyle: SUCCESS_BADGE_STYLE,
      };
    case 'compliant':
      return {
        state,
        label,
        icon: <RiCheckboxCircleLine color="var(--bui-positive-fg-subdued)" />,
        badgeStyle: SUCCESS_BADGE_STYLE,
      };
    case 'in-progress':
      return {
        state,
        label,
        icon: <RiDonutChartLine color="var(--bui-warning-fg-subdued)" />,
        badgeStyle: WARNING_BADGE_STYLE,
      };
    default:
      return {
        state,
        label,
        icon: <RiCloseCircleLine color="var(--bui-negative-fg-subdued)" />,
        badgeStyle: DANGER_BADGE_STYLE,
      };
  }
};

const PLAIN_ICON_COLOR_BY_STATE: Record<TrackRecognitionState, string> = {
  'not-passing': 'var(--bui-fg-negative)',
  'in-progress': 'var(--bui-fg-warning)',
  compliant: 'var(--bui-fg-positive)',
  'fully-compliant': 'var(--bui-fg-positive)',
};

/**
 * Icon for a track's certification state rendered on a plain background
 * (e.g. tab labels). Unlike {@link getTrackRecognition}'s icon, which is
 * colored for placement on a filled badge background.
 */
export const getTrackRecognitionIcon = (
  state: TrackRecognitionState,
  size?: number,
): JSX.Element => {
  const color = PLAIN_ICON_COLOR_BY_STATE[state];
  switch (state) {
    case 'fully-compliant':
      return <RiCheckboxCircleFill color={color} size={size} />;
    case 'compliant':
      return <RiCheckboxCircleLine color={color} size={size} />;
    case 'in-progress':
      return <RiDonutChartLine color={color} size={size} />;
    default:
      return <RiCloseCircleLine color={color} size={size} />;
  }
};

/**
 * Sums passing and total checks across a track's mandatory levels only, for
 * surfacing mandatory-specific progress separately from overall compliance.
 */
export const getMandatoryCheckTotals = (
  track: TrackDefinition,
  trackResult: TrackResult,
): { passedChecks: number; totalChecks: number } => {
  const resultByOrdinal = levelResultsByOrdinal(trackResult);
  const mandatoryLevelResults = getLevelsByImportance(track, 'mandatory')
    .map(level => resultByOrdinal.get(level.ordinal))
    .filter(
      (levelResult): levelResult is TrackLevelResult =>
        levelResult !== undefined,
    );

  return {
    passedChecks: mandatoryLevelResults.reduce(
      (sum, level) => sum + level.passedChecks,
      0,
    ),
    totalChecks: mandatoryLevelResults.reduce(
      (sum, level) => sum + level.totalChecks,
      0,
    ),
  };
};

/**
 * Minimum number of additional passing checks needed across mandatory levels
 * that haven't yet met their pass threshold.
 */
export const getMandatoryChecksRemaining = (
  track: TrackDefinition,
  trackResult: TrackResult,
): number => {
  const resultByOrdinal = levelResultsByOrdinal(trackResult);

  return getLevelsByImportance(track, 'mandatory').reduce((sum, level) => {
    const levelResult = resultByOrdinal.get(level.ordinal);
    if (!levelResult || levelResult.passed || levelResult.totalChecks === 0) {
      return sum;
    }
    return sum + levelResult.totalChecks - levelResult.passedChecks;
  }, 0);
};

/**
 * Returns the CSS foreground color variable or semantic color name for a
 * compliance percentage. 100% is success, 50-99% is warning, below 50% is danger.
 *
 * @param mode - `'css'` (default) returns a CSS variable; `'color'` returns the
 *   semantic token name (`'success'`, `'warning'`, or `'danger'`).
 */
const toStatusColorName = (percentage: number): string => {
  if (percentage >= 100) return 'success';
  if (percentage >= 50) return 'warning';
  return 'danger';
};

export const getStatusColorVar = (
  percentage: number,
  mode: 'css' | 'color' = 'css',
): string => {
  const name = toStatusColorName(percentage);
  return mode === 'color' ? name : `var(--bui-fg-${name})`;
};
